/**
 * ACTIVITY-CORE (L2) — papelera de CATÁLOGO.
 *
 * Solo entidades de catálogo/maestros tienen papelera: menú, variantes,
 * combos, ingredientes, proveedores, empleados, clientes y promociones.
 * El dominio transaccional (órdenes cobradas, pagos, caja, stock) NO pasa por
 * aquí: sus correcciones son operaciones de negocio explícitas que quedan en
 * el historial (variante POS §1b de TRACEABILITY_PLAN).
 */
import {
  and,
  combo,
  customer,
  desc,
  employee,
  eq,
  ingredient,
  isNotNull,
  menu_category,
  menu_product,
  type PowerSyncDrizzleDB,
  product_variant,
  promotion,
  supplier,
} from "@comandero/powersync";

/** Tipos de entidad con papelera (catálogo). */
export type TrashEntityType =
  | "menu_category"
  | "menu_product"
  | "product_variant"
  | "combo"
  | "ingredient"
  | "supplier"
  | "employee"
  | "customer"
  | "promotion";

export const TRASH_ENTITY_TYPES: readonly TrashEntityType[] = [
  "menu_category",
  "menu_product",
  "product_variant",
  "combo",
  "ingredient",
  "supplier",
  "employee",
  "customer",
  "promotion",
] as const;

/** Item normalizado de la papelera (unión de las tablas de catálogo). */
export interface TrashItem {
  entityType: TrashEntityType;
  id: string;
  name: string;
  deletedAt: string;
}

const TRASH_TABLES = {
  menu_category,
  menu_product,
  product_variant,
  combo,
  ingredient,
  supplier,
  employee,
  customer,
  promotion,
} as const;

/**
 * SQL "de vigilancia" para PowerSync: referencia TODAS las tablas de la
 * papelera para que `resolveTables` suscriba la vista a sus cambios (la
 * consulta real es la unión en cliente de `getTrashItemsQuery`).
 */
export function getTrashWatchSql(): { sql: string; params: unknown[] } {
  const sql = TRASH_ENTITY_TYPES.map((t) => `SELECT id FROM ${t}`).join(" UNION ALL ");
  return { sql, params: [] };
}

/**
 * Todos los elementos de catálogo en la papelera de la organización, más
 * recientes primero. Une las tablas en cliente (drizzle SQLite no tiene UNION
 * tipado cómodo y son consultas locales baratas).
 */
export async function getTrashItemsQuery(
  db: PowerSyncDrizzleDB,
  organizationId: string,
): Promise<TrashItem[]> {
  const items: TrashItem[] = [];
  for (const entityType of TRASH_ENTITY_TYPES) {
    const table = TRASH_TABLES[entityType];
    const rows = await db
      .select({ id: table.id, name: table.name, deleted_at: table.deleted_at })
      .from(table)
      .where(and(eq(table.organization_id, organizationId), isNotNull(table.deleted_at)))
      .orderBy(desc(table.deleted_at));
    for (const row of rows) {
      items.push({
        entityType,
        id: row.id,
        name: row.name ?? "",
        deletedAt: row.deleted_at ?? "",
      });
    }
  }
  return items.toSorted((a, b) => (a.deletedAt < b.deletedAt ? 1 : -1));
}
